import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import * as React from 'react';
import PermissionCheck from '../Utilities/PermissionCheck';
import { Permission } from '../Permissions/Permissions';
import Button from '@mui/material/Button';
import { ResponseType, TokenRefreshContext } from '../Contexts/TokenRefreshContext';
import authService from '../api-authorization/AuthorizeService';
import ChangeDialog from '../Utilities/ChangeDialog';
import ActionViewmodel from "../Assets/Viewmodels/ActionViewmodel";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import CommentIcon from "@mui/icons-material/Comment";

interface IProps {
    row: ActionViewmodel;
    refresh: () => void;
    isClient: boolean;
    currentUserid?: string | null;
}
export default function AssetActionsTableRow(props: IProps) {
    const { row, refresh, isClient } = props;
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [outcome, setOutcome] = React.useState('');
    const [openComplete, setOpenComplete] = React.useState(false);
    const [openComment, setOpenComment] = React.useState(false);
    const [openClientComment, setOpenClientComment] = React.useState(false);
    const [openComments, setOpenComments] = React.useState(false);

    React.useEffect(() => {
        setOutcome(row.outcome ?? '');
    }, [row]);

    const markComplete = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Action/MarkComplete?id=${row.id}&outcome=${outcome}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                refresh();
            },
            () => {
            }
        );
    }

    const addComment = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Action/AddComment?id=${row.id}&outcome=${outcome}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                refresh();
            },
            () => {
            }
        );
    }

    const addClientComment = async () => {
        const token = await authService.getAccessToken();

        await crabFetch(`Client/AddComment?id=${row.id}&outcome=${outcome}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                refresh();
            },
            () => {
            }
        );
    }

    const showClientCommentButton = React.useMemo(
        () => props.currentUserid === row.mainEngineerId || props.currentUserid === row.deputyEngineerId,
        [props.currentUserid, row.mainEngineerId, row.deputyEngineerId]
    );

    return (
        <TableRow>
            {!isClient && <TableCell>{row.client}</TableCell>}
            <TableCell>{row.asset}</TableCell>
            <TableCell>{row.type}</TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>{new Date(row.date).toLocaleDateString('en-gb')}</TableCell>
            <TableCell>{row.complete ? 'Completed' : 'Outstanding'}</TableCell>
            <TableCell>
                {row.hasComments ?
                    <IconButton size="small" onClick={() => setOpenComments(true)} aria-label="View comments">
                        <Badge badgeContent={row.comments.length} color="primary">
                            <CommentIcon fontSize="small" />
                        </Badge>
                    </IconButton>
                    : null}
            </TableCell>
            <TableCell>{row.outcome}</TableCell>
            <TableCell align="right">
                {!row.complete &&
                    <Grid container spacing={1} justifyContent="flex-end">
                        {showClientCommentButton &&
                            <Grid item>
                                <Button variant="contained" size="small" onClick={() => setOpenClientComment(true)}>Add Comment</Button>
                            </Grid>
                        }
                        {!isClient &&
                            <React.Fragment>
                                <PermissionCheck permission={Permission.CreateReport}>
                                    <Grid item>
                                        <Button variant="contained" size="small" onClick={() => setOpenComment(true)}>Add Comment</Button>
                                    </Grid>
                                </PermissionCheck>
                                <PermissionCheck permission={Permission.CreateReport}>
                                    <Grid item>
                                        <Button variant="contained" size="small" onClick={() => setOpenComplete(true)}>Mark as Complete</Button>
                                    </Grid>
                                </PermissionCheck>
                            </React.Fragment>
                        }
                    </Grid>
                }
            </TableCell>
            <ChangeDialog
                buttonText='Save'
                text='Add comment'
                open={openComment}
                close={() => { setOpenComment(false); setOutcome(row.outcome ?? ''); }}
                changeDescription={outcome}
                setDescription={setOutcome}
                okClick={addComment}
                placeholder="Please provide details."
            />
            <ChangeDialog
                buttonText='Save'
                text='Mark action as complete'
                open={openComplete}
                close={() => { setOpenComplete(false); setOutcome(row.outcome ?? ''); }}
                changeDescription={outcome}
                setDescription={setOutcome}
                okClick={markComplete}
                placeholder="Please provide details."
            />
            <ChangeDialog
                buttonText='Save'
                text='Add comment'
                open={openClientComment}
                close={() => { setOpenClientComment(false); setOutcome(row.outcome ?? ''); }}
                changeDescription={outcome}
                setDescription={setOutcome}
                okClick={addClientComment}
                placeholder="Please provide details."
            />
            <Dialog open={openComments} onClose={() => setOpenComments(false)} fullWidth maxWidth="sm">
                <DialogTitle>Comments</DialogTitle>
                <DialogContent>
                    {row.comments.length <= 0 &&
                        <Typography>No comments.</Typography>
                    }
                    {row.comments.length > 0 &&
                        <Stack spacing={2}>
                            {row.comments.map((comment, index) =>
                                <Stack key={`${comment.date}-${index}`} spacing={0.5}>
                                    <Typography>{comment.comment}</Typography>
                                    <Typography variant="body2">
                                        {new Date(comment.date).toLocaleDateString('en-gb')} {new Date(comment.date).toLocaleTimeString('en-gb', { hour: '2-digit', minute: '2-digit' })} - {comment.commentedBy}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    }
                    <DialogActions>
                        <Button variant="contained" onClick={() => setOpenComments(false)}>Close</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </TableRow>
    );
}
